import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Cpu, Activity, TrendingUp, Heart, Layers, Radio,
  Target, Zap, Building2, GraduationCap, Trophy, Check, Play,
  Brain, Eye, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import HumeStrip from "@/components/HumeStrip";
import { BRAND, DOMAINS, ENGINES, SPORTS } from "@/lib/biohackerBrand";
import { useSportContext } from "@/lib/useSportContext";

/**
 * Landing — BioHacker Sports™ · The Athlete Performance Operating System™
 * ─────────────────────────────────────────────────────────────────────
 * Rebrand of the ArmIQ landing preserving the Bloomberg-terminal aesthetic
 * (dense data, quiet palette, cinematic hero video) while re-scoping the
 * messaging from "baseball application" to "enterprise intelligence platform".
 *
 * IA:
 *   Hero (rotating sport hero video) → Domains → Engines → Sport Packages
 *   → Solutions preview → Testimonials → Enterprise CTA
 */

// Domain icon map (component references — the const list stays in lib/)
const DOMAIN_ICONS = {
  movement: Activity, performance: TrendingUp, health: Heart, recovery: Layers,
  workload: Zap, development: TrendingUp, mental: Brain, vision: Eye,
};

// Live ticker (mission-control aesthetic) — now cross-sport.
const TICKER = [
  "MOVEMENT 87 · PERFORMANCE 92 · WORKLOAD 71 · RECOVERY 84",
  "ARM STRESS -12% · SPRINT +0.08s · HRV +8ms",
  "23 SPORTS · 41,208 ATHLETES · 1 INTELLIGENCE LAYER",
  "PREDICTIVE INJURY RISK · LOW · 3 DRIVERS",
  "SPINE ANGLE 32.5° · POSTURE RETENTION 94% · BAT SPEED 71 mph",
  "ROSTER READINESS 88% · 4 ATHLETES FLAGGED",
];

// Hero video rotation — one per Sport Intelligence Package (fallback ok
// if the file isn't present).
const HERO_VIDEOS = SPORTS.map((s) => ({ id: s.id, name: s.name, src: s.hero_video, tag: s.tag }));

export default function Landing() {
  const videoRefs = useRef([]);
  const [videoIdx, setVideoIdx] = useState(0);

  // Rotate the hero video every ~9s for a cinematic multi-sport feel.
  useEffect(() => {
    const t = setInterval(() => setVideoIdx((i) => (i + 1) % HERO_VIDEOS.length), 9000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#050810] text-white overflow-x-hidden" data-testid="landing-biohacker">
      <SiteNav dark />
      <Hero videoIdx={videoIdx} setVideoIdx={setVideoIdx} videoRefs={videoRefs} />
      <Ticker />
      <DomainsSection />
      <HumeStrip />
      <EnginesSection />
      <SportPackagesSection />
      <MentalPerformanceTeaser />
      <SolutionsPreview />
      <StatsBar />
      <Testimonials />
      <EnterpriseCTA />
      <SiteFooter />
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────
function Hero({ videoIdx, setVideoIdx, videoRefs }) {
  const active = HERO_VIDEOS[videoIdx];
  const sportCtx = useSportContext();
  const eyebrow = sportCtx.isNeutral
    ? BRAND.parentTrademark
    : `${sportCtx.sport.name} · ${BRAND.parentTrademark}`;
  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        {HERO_VIDEOS.map((v, i) => (
          <video
            key={v.id}
            ref={(el) => (videoRefs.current[i] = el)}
            src={v.src}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              i === videoIdx ? "opacity-40" : "opacity-0"
            }`}
            autoPlay muted loop playsInline
            data-testid={`hero-video-${v.id}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050810]/60 via-[#050810]/40 to-[#050810]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="max-w-4xl">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-4 flex items-center gap-2"
            data-testid="hero-eyebrow">
            <Sparkles className="h-3.5 w-3.5" /> {eyebrow}
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white"
            data-testid="hero-headline">
            The Athlete Performance<br/>
            <span className="bg-gradient-to-r from-fuchsia-400 via-red-400 to-amber-300 bg-clip-text text-transparent">
              Operating System™
            </span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
            One Intelligence Platform. Every Athlete. Every Team. Every Sport.
            <br className="hidden md:block" />
            <span className="text-white/50">
              BioHacker Sports™ unifies movement, performance, workload, recovery, health, mental
              wellness, vision, and development into a single decision layer for the entire organization.
            </span>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-demo" data-testid="hero-cta-demo">
              <Button size="lg" className="text-white font-bold text-sm px-6 py-6"
                style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/platform" data-testid="hero-cta-platform">
              <Button size="lg" variant="ghost"
                className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                Explore the platform
              </Button>
            </Link>
          </div>

          {/* Sport tabs — switch the hero video */}
          <div className="mt-12 flex flex-wrap gap-2" data-testid="hero-sport-tabs">
            {HERO_VIDEOS.map((v, i) => (
              <button
                key={v.id}
                onClick={() => setVideoIdx(i)}
                className={`text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 rounded-full border transition-colors ${
                  i === videoIdx
                    ? "border-fuchsia-400/50 bg-fuchsia-400/10 text-fuchsia-200"
                    : "border-white/15 text-white/50 hover:text-white hover:border-white/30"
                }`}
                data-testid={`hero-tab-${v.id}`}
              >
                {v.name.replace("BioHacker ", "").replace("™", "")}
              </button>
            ))}
          </div>
          <div className="mt-3 text-[11px] font-mono-num text-white/45" data-testid="hero-active-tag">
            {active.name} · {active.tag}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Ticker ────────────────────────────────────────────────────────────
function Ticker() {
  return (
    <div className="border-y border-white/8 bg-black/40 overflow-hidden" data-testid="live-ticker">
      <div className="flex whitespace-nowrap animate-[scroll_40s_linear_infinite]">
        {[...TICKER, ...TICKER].map((t, i) => (
          <span key={i} className="text-[10px] tracking-[0.3em] uppercase font-mono-num text-white/55 px-8 py-3">
            <Radio className="inline h-3 w-3 mr-2 text-red-500" />{t}
          </span>
        ))}
      </div>
      <style>{`@keyframes scroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }`}</style>
    </div>
  );
}

// ── The Athlete Intelligence Platform — 8 Domains ─────────────────────
function DomainsSection() {
  return (
    <section id="domains" className="py-24 border-b border-white/8" data-testid="domains-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            The Athlete Intelligence Platform™
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Eight Intelligence Domains™.<br/>
            <span className="text-white/50">One continuously evolving profile.</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl">
            Every athlete begins with the {BRAND.assessment}. It builds their {BRAND.profile}, stored
            in the {BRAND.cloud}, and powers every downstream Intelligence Engine™.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {DOMAINS.map((d) => {
            const Icon = DOMAIN_ICONS[d.id] || Activity;
            return (
              <div key={d.id}
                className="ink-card rounded-xl p-5 border-l-2 hover:border-l-4 transition-all"
                style={{ borderLeftColor: d.tone }}
                data-testid={`domain-${d.id}`}
              >
                <Icon className="h-5 w-5 mb-3" style={{ color: d.tone }} />
                <div className="font-display font-black text-base text-white leading-tight">{d.label}</div>
                <div className="mt-2 text-xs text-white/50 leading-relaxed">{d.tag}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Powered by Intelligence Engines™ ────────────────────────────────
function EnginesSection() {
  const [openId, setOpenId] = useState(ENGINES[0].id);
  const active = ENGINES.find((e) => e.id === openId) || ENGINES[0];
  return (
    <section id="engines" className="py-24 border-b border-white/8" data-testid="engines-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            Powered by Intelligence Engines™
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Eleven engines.<br/>
            <span className="text-white/50">One shared intelligence layer.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-[420px,1fr] gap-6">
          <div className="grid grid-cols-2 gap-2" data-testid="engine-tile-grid">
            {ENGINES.map((e) => (
              <button
                key={e.id}
                onClick={() => setOpenId(e.id)}
                className={`text-left rounded-lg border p-3 transition-all ${
                  e.id === openId
                    ? "border-white/40 bg-white/[0.06]"
                    : "border-white/8 bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid={`engine-tile-${e.id}`}
              >
                <span className="block h-2 w-2 rounded-full mb-2" style={{ background: e.tone, boxShadow: `0 0 10px ${e.tone}` }} />
                <div className="font-display font-black text-sm text-white leading-tight">{e.name}</div>
              </button>
            ))}
          </div>
          <div className="ink-card rounded-2xl p-8" data-testid={`engine-panel-${active.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-3 w-3 rounded-full" style={{ background: active.tone, boxShadow: `0 0 16px ${active.tone}` }} />
              <div className="font-display font-black text-2xl text-white tracking-tight">{active.name}</div>
            </div>
            <Detail label="What it measures" value={active.measures} />
            <Detail label="Why it matters"   value={active.matters} />
            <div className="mt-5">
              <div className="text-[9px] uppercase tracking-[0.3em] text-white/45 mb-2">Activated in</div>
              <div className="flex flex-wrap gap-2">
                {active.sports.map((s) => (
                  <span key={s} className="text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-white/12 text-white/75">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function Detail({ label, value }) {
  return (
    <div className="mt-3">
      <div className="text-[9px] uppercase tracking-[0.3em] text-white/45 mb-1">{label}</div>
      <div className="text-sm text-white/85 leading-relaxed">{value}</div>
    </div>
  );
}

// ── Sport Intelligence Packages™ ────────────────────────────────────
function SportPackagesSection() {
  return (
    <section id="sports" className="py-24 border-b border-white/8" data-testid="sports-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            Sport Intelligence Packages™
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Every sport is a package.<br/>
            <span className="text-white/50">Same platform. Sport-specific intelligence.</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SPORTS.map((s) => (
            <Link key={s.id} to={s.href}
              className="ink-card rounded-xl overflow-hidden group hover:border-white/25 transition-all"
              data-testid={`sport-card-${s.id}`}
            >
              <div className="aspect-video bg-black relative overflow-hidden">
                <video src={s.hero_video} muted loop playsInline autoPlay
                  className="w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050810] via-transparent to-transparent" />
                {s.flagship && (
                  <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/50 text-fuchsia-200">
                    Flagship
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="font-display font-black text-base text-white leading-tight">{s.name}</div>
                <div className="mt-1 text-[11px] text-white/50 leading-snug">{s.tag}</div>
                {s.hover_metric && (
                  <div
                    className="mt-3 rounded-lg border border-white/8 bg-white/[0.02] p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`sport-hover-metric-${s.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[9px] tracking-[0.2em] uppercase text-white/45">{s.hover_metric.label}</span>
                      <span className="font-mono-num font-black text-white text-sm tabular-nums">{s.hover_metric.value}</span>
                    </div>
                    <div className="text-[9px] font-mono-num text-white/40 mt-0.5">{s.hover_metric.ctx}</div>
                  </div>
                )}
                <div className="mt-3 text-[9px] uppercase tracking-[0.25em] text-white/45 group-hover:text-white transition-colors flex items-center gap-1">
                  Explore package <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Mental Performance & Wellness Intelligence teaser ─────────────────
function MentalPerformanceTeaser() {
  return (
    <section className="py-24 border-b border-white/8 relative overflow-hidden" data-testid="mental-teaser">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-cyan-500/20 blur-[120px]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-fuchsia-500/20 blur-[120px]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-cyan-300/80 mb-3 flex items-center gap-2">
            <Brain className="h-3.5 w-3.5" /> Mental Performance & Wellness Intelligence™
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            The wellness engine every program says they want.
            <br/>
            <span className="text-white/50">And so few actually operationalize.</span>
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed">
            The MP&W Intelligence™ module tracks cognitive load, mood, stress resilience,
            sleep-latency signals, and emotional readiness — and ties every reading to the athlete&apos;s
            physical fatigue, workload, and injury risk. Coaches see the whole athlete, not just the metrics.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-white/80">
            {[
              "Daily wellness triage across the entire roster",
              "Individual resilience trend lines with anonymized reporting to medical",
              "Cognitive-load impact on Vision & Decision Intelligence™",
              "Automatic escalation to sports psychologists when patterns break",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-cyan-300" /> {line}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Link to="/mental-performance" data-testid="mental-cta">
              <Button className="text-white font-bold text-sm px-5 py-5"
                style={{ backgroundColor: "var(--brand-secondary)" }}>
                Explore the module <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: "Mood index",         v: "7.4/10", tag: "-0.3 vs baseline", tone: "#22D3EE" },
            { k: "Stress load",        v: "MED",    tag: "elevated · 5 days", tone: "#F59E0B" },
            { k: "Sleep latency",      v: "22 min", tag: "vs 14 min baseline", tone: "#A78BFA" },
            { k: "Resilience",         v: "82",     tag: "-4 vs peer group",   tone: "#10B981" },
            { k: "Cognitive load",     v: "HIGH",   tag: "practice heaviness", tone: "#EF4444" },
            { k: "Readiness alignment",v: "68%",    tag: "physical vs mental", tone: "#38BDF8" },
          ].map((tile) => (
            <div key={tile.k} className="ink-card rounded-xl p-4">
              <div className="text-[9px] tracking-[0.25em] uppercase text-white/45">{tile.k}</div>
              <div className="mt-1 font-display font-black text-2xl font-mono-num" style={{ color: tile.tone }}>{tile.v}</div>
              <div className="text-[10px] text-white/45 mt-1">{tile.tag}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Solutions preview ─────────────────────────────────────────────────
function SolutionsPreview() {
  const items = [
    { icon: Trophy,        label: "Team Coaches",                to: "/solutions#coach" },
    { icon: Activity,      label: "Athletic Trainers",           to: "/solutions#trainer" },
    { icon: Zap,           label: "Strength & Conditioning",     to: "/solutions#sc" },
    { icon: Heart,         label: "Sports Medicine",             to: "/solutions#medicine" },
    { icon: Building2,     label: "Athletic Directors",          to: "/solutions#ad" },
    { icon: GraduationCap, label: "Performance Directors",       to: "/solutions#performance_director" },
    { icon: Target,        label: "Professional Organizations",  to: "/solutions#pro" },
    { icon: Cpu,           label: "Military Human Performance",  to: "/solutions#military" },
  ];
  return (
    <section className="py-24 border-b border-white/8" data-testid="solutions-preview">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Solutions by role</div>
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
          Built for every decision-maker.
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <Link key={it.label} to={it.to}
              className="ink-card rounded-xl p-4 hover:border-white/25 transition-all group"
              data-testid={`solution-${it.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
            >
              <it.icon className="h-5 w-5 text-white/70 group-hover:text-white transition-colors" />
              <div className="mt-3 font-display font-black text-white text-sm">{it.label}</div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/40 group-hover:text-white transition-colors">
                See workflow →
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/solutions" data-testid="solutions-cta">
            <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm">
              All solutions <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Stats + testimonials + enterprise ─────────────────────────────────
function StatsBar() {
  const stats = [
    { k: "Sports",        v: "23"    },
    { k: "Athletes",      v: "41k+"  },
    { k: "Data points/day", v: "9.2M" },
    { k: "Departments",   v: "60+"   },
  ];
  return (
    <section className="py-16 border-b border-white/8 bg-black/20" data-testid="stats-bar">
      <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.k} className="text-center">
            <div className="font-display font-black text-4xl sm:text-5xl text-white tracking-tight tabular-nums">{s.v}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/45">{s.k}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const quotes = [
    {
      quote: "BioHacker Sports gave our department a shared record — trainers, S&C, and coaches all live in the same intelligence layer now.",
      who: "Dr. M. Alvarez",
      role: "Director of Sports Medicine · D1 Program",
    },
    {
      quote: "It's the first athlete platform my athletic director, my strength staff, and my head coach all agreed on.",
      who: "Coach T. Reed",
      role: "Athletic Director · High School Program",
    },
    {
      quote: "The intelligence engines architecture is what enterprise software should have been for years.",
      who: "J. Chen",
      role: "Performance Director · Pro Organization",
    },
  ];
  return (
    <section className="py-20 border-b border-white/8" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Trusted across the enterprise</div>
        <h2 className="font-display font-black text-3xl text-white tracking-tight">The intelligence layer coaches actually adopt.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {quotes.map((q, i) => (
            <blockquote key={i} className="ink-card rounded-2xl p-6" data-testid={`testimonial-${i}`}>
              <div className="text-white/85 text-[15px] leading-relaxed">&ldquo;{q.quote}&rdquo;</div>
              <div className="mt-4 text-[11px] font-mono-num text-white/45">— {q.who} · {q.role}</div>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

function EnterpriseCTA() {
  return (
    <section className="py-24 relative overflow-hidden" data-testid="enterprise-cta">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-red-600/20 blur-[140px]" />
      </div>
      <div className="max-w-4xl mx-auto px-6 text-center relative">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-4">The enterprise layer</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
          One intelligence platform.<br/>
          <span className="bg-gradient-to-r from-fuchsia-400 via-red-400 to-amber-300 bg-clip-text text-transparent">
            Every athlete, team, and sport in your organization.
          </span>
        </h2>
        <p className="mt-6 text-white/70 max-w-2xl mx-auto leading-relaxed">
          BioHacker Sports™ is the intelligence layer that transforms disconnected performance data
          into actionable decision-making for individuals, teams, departments, and enterprises.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/request-demo" data-testid="footer-cta-demo">
            <Button size="lg" className="text-white font-bold text-sm px-6 py-6"
              style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/enterprise" data-testid="footer-cta-enterprise">
            <Button size="lg" variant="ghost"
              className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
              Enterprise overview
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
